import { useEffect, useState } from 'react';
import { Publication } from '@amzn/awscat-pattern-library-service-model';

import { useMplServiceClient } from '../../contexts/MplServiceClientContext';
import { log } from '../../loggers/Logger';

const FLUSH_INTERVAL_MS = 250;

/** The publications downloaded so far, and whether the last one has arrived. */
export interface PublicationStream {
  /** Publications in the order the server sent them. */
  readonly publications: readonly Publication[];
  /** True once the download ends, whether it succeeded or failed. */
  readonly isFinished: boolean;
  /** Display-ready message when the download failed, or undefined otherwise. */
  readonly errorMessage: string | undefined;
}

/**
 * Downloads every publication and re-renders with what has arrived at most every 250ms, so pages
 * can show content before the download ends; pass `isEnabled = false` to skip the download.
 */
export function usePublicationStream(isEnabled = true): PublicationStream {
  const client = useMplServiceClient();
  const [publications, setPublications] = useState<readonly Publication[]>([]);
  const [isFinished, setIsFinished] = useState(!isEnabled);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  // When the page needs publications, then download them and show each group as it arrives.
  useEffect(() => {
    if (!isEnabled) {
      return undefined;
    }

    const received: Publication[] = [];
    let shownCount = 0;
    let isUnmounted = false;

    // Skip the refresh when nothing new arrived since the last one.
    function flush() {
      if (received.length === shownCount) {
        return;
      }
      shownCount = received.length;
      setPublications([...received]);
    }

    // Refresh the page every 250ms with whatever has arrived so far.
    const flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

    // Leaving the page ends the loop at the next batch, which also stops the download.
    async function download() {
      for await (const batch of client.streamPublicationsThroughS3()) {
        if (isUnmounted) {
          return;
        }
        received.push(...batch.publications);
      }
    }

    // Once the download ends, show everything. Skip it after unmount or a re-run, so an old download can't overwrite a newer one.
    function finish() {
      clearInterval(flushTimer);
      if (isUnmounted) {
        return;
      }
      flush();
      setIsFinished(true);
    }

    // A failed download keeps what already arrived and tells the page, so it can say the list is incomplete.
    function fail(error: unknown) {
      log.error({ err: error }, 'Failed to download publications');
      finish();
      if (isUnmounted) {
        return;
      }
      setErrorMessage(
        `Failed to load all content: ${error instanceof Error ? error.message : 'unknown error'}`,
      );
    }

    setIsFinished(false);
    setErrorMessage(undefined);
    download().then(finish, fail);

    return () => {
      isUnmounted = true;
      clearInterval(flushTimer);
    };
  }, [client, isEnabled]);

  // Turning the download off counts as finished, even after one had started.
  return { publications, isFinished: isFinished || !isEnabled, errorMessage };
}
